import React, { useContext, useEffect, useState } from "react";
import "./style.css";

import { useNavigate } from "react-router-dom";
import { getDate } from "../../Library/datetime";
import Title from "../../Components/Atoms/Title";
import { PopUpModal } from "../../Components/Atoms";
import { Button, SimpleGrid, useToast } from "@chakra-ui/react";
import { discontinueModelRequest, discontinueModel, getAllModels } from "../../APIs";
import { AddModelIcon, EditModelIcon } from "../../Assets/images/svgs";
import PATHS from "../../Routes/paths"

import { AuthContext } from "../../Library/contexts"

const Models = () => {
  const toast = useToast();

  const [allModels, setAllModels] = useState([]);
  const [confirmStatusChange, setConfirmStatusChange] = useState(null);

  const [invalidOTP, setInvalidOTP] = useState("");

  const navigate = useNavigate();
  const { profile } = useContext(AuthContext);

  useEffect(() => {
    getAllModels()
    .then(data => setAllModels(data))
    .catch(error => console.error(error))
  }, []);

  const sendOTPandUpdateModal = async (event, model, index) => {
    const body = { modelId: model.idx };
    try {
      const response = await discontinueModelRequest(body);

      if (response.time_left) {
        const string = `OTP already sent.`;
        toast({ title: string, variant: "subtle", position: "bottom-right" });
      } else {
        toast({ title: response, variant: "subtle", position: "bottom-right" });
      }

      setConfirmStatusChange({
        open: true,
        modalMsg: (
          <p>
            Enter 6-digit OTP sent to your number {profile ? profile.mob : ""} to{" "}
            <span className="model-status-indicator"> Discontinue </span>{" "}
            the model "{model.nm}"
          </p>
        ),
        footer: "model-status-modal-footer",
        showInput: true,
        inputCover: "toggle-status-otp-input",
        placeholder: "Enter OTP",
        closeBtn: "Cancel",
        confirmBtn: "Submit",
        onModalClose: () => {
          setConfirmStatusChange(null);
          setInvalidOTP("");
        },
        onConfirm: (val) => toggleModelStatus(val, index),
      });
    } catch(e) {
      setConfirmStatusChange(null);
      setInvalidOTP("");
      console.error("Error in sendOTP", e);
      toast({ title: "Something went wrong.", variant: "subtle", position: "bottom-right" });
    }
  };

  const handleStatusChange = (event, model, index) => {
    if (model.dcnt) return;

    setConfirmStatusChange({
      open: true,
      modalMsg: `Are you sure you want to discontinue model "${model.nm}"?`,
      closeBtn: "Cancel",
      confirmBtn: "Confirm",
      onModalClose: () => {
        setConfirmStatusChange(null);
        setInvalidOTP("");
      },
      onConfirm: () => sendOTPandUpdateModal(event, model, index),
    });
  };

  const toggleModelStatus = async (verificationOTP, index) => {
    if (!verificationOTP) {
      setInvalidOTP("Invalid OTP");
      return;
    }

    try {
      await discontinueModel({ otp: verificationOTP });
      setInvalidOTP("");
      setConfirmStatusChange(null);

      setAllModels((old) => {
        let newModels = [...old];
        newModels[index] = { ...old[index], dcnt: true };
        return newModels;
      });

      toast({ title: "Model Discontinued Successfully.", variant: "subtle", position: "bottom-right" });
    } catch (e) {
      setInvalidOTP("Invalid OTP");
    }
  };

  const joinArray = (array) => {
    if (!array) return "";
    return array.map(({ nm }) => nm).join(", ");
  };

  const modelBasicSpecs = [
    { label: "Added Date", key: "cdt", method: (ts) => getDate(ts, true) },
    { label: "Modes", key: "modes", method: joinArray },
    { label: "Colours", key: "colors", method: joinArray },
  ];

  const modelMotorSpecs = [
    { label: "Torque", key: "torque", unit: "Nm" },
    { label: "RPM", key: "rpm" },
    { label: "Rated Power", key: "ratedPower", unit: "W" },
    { label: "Rated Voltage", key: "ratedVoltage", unit: "V" },
    { label: "Rated Voltage", key: "ratedVoltage", unit: "V" },
  ];

  const modelBatterySpecs = [
    { label: "Capacity", key: "cap", unit: "wh" },
    { label: "Operating Temp", key: "operatingTemp", unit: "°C" },
    { label: "Charging Current", key: "chargeCurr", unit: "V" },
    { label: "Discharging Current", key: "dischargeCurr", unit: "V" },
  ];

  const handleEdit = (model) => {
    navigate(`${PATHS.ADDMODEL}?model=${model.idx}`, { state: { model } });
  };

  return (
    <div className="model-settings">
      <Button
        mb="8px"
        leftIcon={<AddModelIcon />}
        className="add-new-modal-btn"
        onClick={() => navigate(PATHS.ADDMODEL)}
      >
        Add New Model
      </Button>
      <SimpleGrid
        spacing={6}
        templateColumns="repeat(auto-fit, minmax(350px, 1fr))"
        gridAutoColumns
      >
        {allModels.map((model, idx) => (
          <div className="model-card" key={idx}>
            <div className="card-header">
              <Title> {model.nm} </Title>
              <Button
                onClick={(e) => handleStatusChange(e, model, idx)}
                name={`button-${model.idx}`}
                className={!model.dcnt ? "model-active-status" : "model-discontinued-status"}
              >
                {!model.dcnt ? "Active" : "Discontinued"}
              </Button>
              {!model.dcnt && (
                <Button
                  leftIcon={<EditModelIcon />}
                  className="edit-button"
                  onClick={() => handleEdit(model)}
                >
                  Edit
                </Button>
              )}
            </div>
            {modelBasicSpecs.map((spec, idx) => (
              <div key={idx}>
                <label>
                  {spec.label}
                  <span>{spec.method(model[spec.key])}</span>
                </label>
                <br />
              </div>
            ))}

            <div className="card-content">
              <div className="card-content-left">
                <p className="content-header">Motor Specification</p>
                {modelMotorSpecs.map((spec, idx) => (
                  <div key={idx}>
                    <label>
                      {spec.label}
                      <span>
                        {spec.method ? spec.method(model.motor[spec.key]) : model.motor[spec.key]}
                        {" "}
                        {spec.unit}
                      </span>
                    </label>
                    <br />
                  </div>
                ))}
              </div>
              <div className="card-content-right">
                <p className="content-header">Battery Specification</p>
                {modelBatterySpecs.map((spec, idx) => (
                  <div key={idx}>
                    <label>
                      {spec.label}
                      <span>
                        {spec.method ? spec.method(model.battery[spec.key]) : model.battery[spec.key]}
                        {" "}
                        {spec.unit}
                      </span>
                    </label>
                    <br />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </SimpleGrid>

      <PopUpModal {...confirmStatusChange} hasError={invalidOTP} />
    </div>
  );
};

export default Models;
